/**
 * Subscriber panel — DataTable list + add/edit dialog + delete confirm.
 * Scope drives key label/placeholder and validation.
 */
import { useState } from 'react';
import { Mail, Send, Webhook, Users, Slack, Plus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Button,
  Badge,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  useSubscribers,
  useCreateSubscriber,
  useUpdateSubscriber,
  useDeleteSubscriber,
  type Subscriber,
  type SubscriberScope,
  type SubscriberStatus,
} from '@/api/healthcheck';
import { RowMenu } from '@/pages/services/components/RowMenu';
import { SubscriberStatusBadge } from './SubscriberStatusBadge';

// ---- Scope metadata ------------------------------------------------------

interface ScopeMeta {
  label: string;
  icon: LucideIcon;
  keyLabel: string;
  keyPlaceholder: string;
  /** Helper text shown below the key input (where it comes from). */
  keyHelper: string;
  /** Returns error string if invalid, undefined if valid */
  validate: (key: string) => string | undefined;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+/;
const NUMERIC_RE = /^-?\d+$/;

const SCOPE_META: Record<SubscriberScope, ScopeMeta> = {
  telegram: {
    label: 'Telegram',
    icon: Send,
    keyLabel: 'Chat ID',
    keyPlaceholder: '-100123456789',
    keyHelper: 'The chat/group/channel ID that receives messages (e.g. -100123456789).',
    validate: (k) => (NUMERIC_RE.test(k.trim()) ? undefined : 'Must be a numeric Telegram chat ID.'),
  },
  email: {
    label: 'Email',
    icon: Mail,
    keyLabel: 'Email address',
    keyPlaceholder: 'user@example.com',
    keyHelper: "The recipient's email address.",
    validate: (k) => (EMAIL_RE.test(k.trim()) ? undefined : 'Enter a valid email address.'),
  },
  webhook: {
    label: 'Webhook',
    icon: Webhook,
    keyLabel: 'Webhook URL',
    keyPlaceholder: 'https://example.com/webhook',
    keyHelper: 'The destination URL that receives the signed JSON POST.',
    validate: (k) => (URL_RE.test(k.trim()) ? undefined : 'Enter a valid https:// URL.'),
  },
  teams: {
    label: 'Teams',
    icon: Users,
    keyLabel: 'Webhook URL',
    keyPlaceholder: 'https://xxx.webhook.office.com/...',
    keyHelper: 'The Teams Incoming Webhook URL for this recipient\'s channel.',
    validate: (k) => (URL_RE.test(k.trim()) ? undefined : 'Enter a valid https:// URL.'),
  },
  slack: {
    label: 'Slack',
    icon: Slack,
    keyLabel: 'Webhook URL',
    keyPlaceholder: 'https://hooks.slack.com/services/...',
    keyHelper: "The Slack Incoming Webhook URL for this recipient's channel.",
    validate: (k) => (URL_RE.test(k.trim()) ? undefined : 'Enter a valid https:// URL.'),
  },
};

const STATUS_OPTIONS: SubscriberStatus[] = ['pending', 'verified', 'disabled'];

// ---- Add/Edit dialog -----------------------------------------------------

interface SubFormState {
  scope: SubscriberScope;
  key: string;
  name: string;
  status: SubscriberStatus;
}

function makeDefaultForm(allowedScopes: SubscriberScope[]): SubFormState {
  const defaultScope = allowedScopes.includes('email') ? 'email' : (allowedScopes[0] ?? 'email');
  return { scope: defaultScope, key: '', name: '', status: 'pending' };
}

interface SubDialogProps {
  channelId: string;
  allowedScopes: SubscriberScope[];
  editing: Subscriber | null;
  onClose: () => void;
}

function SubscriberDialog({ channelId, allowedScopes, editing, onClose }: SubDialogProps) {
  const [form, setForm] = useState<SubFormState>(
    editing
      ? { scope: editing.scope, key: editing.key, name: editing.name ?? '', status: editing.status }
      : makeDefaultForm(allowedScopes),
  );
  const [showErrors, setShowErrors] = useState(false);

  const patch = (p: Partial<SubFormState>) => setForm((s) => ({ ...s, ...p }));

  const createSub = useCreateSubscriber(channelId, { onSuccess: onClose });
  const updateSub = useUpdateSubscriber(channelId, editing?.id ?? '', { onSuccess: onClose });

  const meta = SCOPE_META[form.scope];
  const keyErr = showErrors && !form.key.trim() ? 'Key is required.' : (showErrors ? meta.validate(form.key) : undefined);
  const busy = createSub.isPending || updateSub.isPending;
  const apiError = createSub.error ?? updateSub.error;

  const submit = () => {
    const keyValidErr = meta.validate(form.key);
    if (!form.key.trim() || keyValidErr) {
      setShowErrors(true);
      return;
    }
    const payload = {
      scope: form.scope,
      key: form.key.trim(),
      name: form.name.trim() || undefined,
      status: form.status,
    };
    if (editing) {
      updateSub.mutate(payload);
    } else {
      createSub.mutate(payload);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit subscriber' : 'Add subscriber'}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {apiError && (
            <Alert tone="danger" title={(apiError as Error).message || 'Request failed'} />
          )}
          <Field label="Scope" required>
            {() => (
              allowedScopes.length === 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', height: 36 }}>
                  <Badge tone="neutral" appearance="subtle">{SCOPE_META[allowedScopes[0]].label}</Badge>
                </div>
              ) : (
                <Select
                  value={form.scope}
                  onValueChange={(v) => patch({ scope: v as SubscriberScope, key: '' })}
                  disabled={!!editing}
                >
                  <SelectTrigger style={{ maxWidth: 240 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedScopes.map((s) => (
                      <SelectItem key={s} value={s}>
                        {SCOPE_META[s].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            )}
          </Field>

          <Field
            label={meta.keyLabel}
            required
            description={meta.keyHelper}
            error={keyErr}
          >
            {(p) => (
              <Input
                {...p}
                value={form.key}
                onChange={(e) => patch({ key: e.target.value })}
                placeholder={meta.keyPlaceholder}
                invalid={!!keyErr}
                style={{ maxWidth: 340 }}
              />
            )}
          </Field>

          <Field label="Display name" description="Optional human-friendly label.">
            {(p) => (
              <Input
                {...p}
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="e.g. On-call team"
                style={{ maxWidth: 340 }}
              />
            )}
          </Field>

          <Field label="Status">
            {() => (
              <Select value={form.status} onValueChange={(v) => patch({ status: v as SubscriberStatus })}>
                <SelectTrigger style={{ maxWidth: 180 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} style={{ textTransform: 'capitalize' }}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy}>
          {editing ? 'Save changes' : 'Add subscriber'}
        </Button>
      </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- DataTable skeleton rows for loading state ---------------------------

function SubscriberSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} style={{ height: 40, borderRadius: 'var(--vn-radius-md)' }} />
      ))}
    </div>
  );
}

// ---- Subscriber panel ----------------------------------------------------

interface Props {
  channelId: string;
  /** Scopes the channel can accept. Single-type channel passes [type]; multi-type passes all. */
  allowedScopes: SubscriberScope[];
}

export function SubscriberPanel({ channelId, allowedScopes }: Props) {
  const { data: subs = [], isLoading, isError } = useSubscribers(channelId);
  const deleteSub = useDeleteSubscriber(channelId);

  const [dialogTarget, setDialogTarget] = useState<Subscriber | 'new' | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Subscriber | null>(null);

  const columns: ColumnDef<Subscriber, unknown>[] = [
    {
      id: 'destination',
      header: 'Destination',
      cell: ({ row }) => {
        const sub = row.original;
        const m = SCOPE_META[sub.scope];
        const Icon = m.icon;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Icon size={14} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sub.name ?? sub.key}
              </div>
              {sub.name && (
                <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {sub.key}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'scope',
      header: 'Type',
      cell: ({ row }) => (
        <Badge tone="neutral" appearance="subtle" size="sm">
          {SCOPE_META[row.original.scope].label}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <SubscriberStatusBadge status={row.original.status} />,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'Edit', onSelect: () => setDialogTarget(row.original) },
            { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
          ]}
        />
      ),
    },
  ];

  if (isError) {
    return <Alert tone="danger" title="Couldn't load subscribers" />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--vn-space-3)' }}>
        <span style={{ fontWeight: 600 }}>Subscribers</span>
        <Button
          size="sm"
          variant="secondary"
          startIcon={<Plus size={14} />}
          onClick={() => setDialogTarget('new')}
        >
          Add
        </Button>
      </div>

      {isLoading ? (
        <SubscriberSkeleton />
      ) : (
        <DataTable
          columns={columns}
          data={subs}
          getRowId={(s) => s.id}
          emptyState={
            <EmptyState
              icon={<Mail size={24} />}
              title="No subscribers"
              description="Add a recipient to start receiving alerts."
              actions={
                <Button size="sm" startIcon={<Plus size={14} />} onClick={() => setDialogTarget('new')}>
                  Add subscriber
                </Button>
              }
            />
          }
        />
      )}

      {dialogTarget !== null && (
        <SubscriberDialog
          channelId={channelId}
          allowedScopes={allowedScopes}
          editing={dialogTarget === 'new' ? null : dialogTarget}
          onClose={() => setDialogTarget(null)}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title="Remove subscriber?"
        description={
          pendingDelete
            ? `"${pendingDelete.name ?? pendingDelete.key}" will be removed from this channel.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={() => {
          if (!pendingDelete) return;
          const sub = pendingDelete;
          setPendingDelete(null);
          deleteSub.mutate(sub.id);
        }}
      />
    </div>
  );
}
